import { ChangeEvent, useState } from "react";

import { useTranslation } from "react-i18next";

export interface LabelFields {
  carrier: string;
  tracking: string;
  labelFileName?: string;
  labelFileBase64?: string;
}

export const EMPTY_LABEL: LabelFields = { carrier: "", tracking: "" };

/** Carriers GP Metals actually sends with; "Other" lets the name be typed. */
const CARRIERS = ["Royal Mail", "DPD", "Proovia", "Parcelforce", "Evri", "FedEx", "UPS", "DHL"];

const LABEL_FILE_TYPES = ".pdf,.jpg,.jpeg,.png";

export const labelFieldsComplete = (f: LabelFields) => !!f.carrier.trim() && !!f.tracking.trim() && !!f.labelFileBase64;

/** The file's contents without the data: prefix, which is what eBay's file upload wants. */
function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      resolve(result.includes(",") ? result.slice(result.indexOf(",") + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

interface LabelFileFieldsProps {
  value: LabelFields;
  onChange: (value: LabelFields) => void;
  disabled?: boolean;
}

/** Carrier, tracking number and the label file (PDF/JPG/PNG) for a label of our own. */
export default function LabelFileFields({ value, onChange, disabled = false }: LabelFileFieldsProps) {
  const { t } = useTranslation("returns");
  const [otherCarrier, setOtherCarrier] = useState(!!value.carrier && !CARRIERS.includes(value.carrier));
  const [fileError, setFileError] = useState<string | null>(null);

  const pickFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const base64 = await readAsBase64(file);
      setFileError(null);
      onChange({ ...value, labelFileName: file.name, labelFileBase64: base64 });
    } catch {
      setFileError(t("That file couldn't be read – try again."));
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-[140px_minmax(0,1fr)] gap-2">
        {otherCarrier ? (
          <input
            type="text"
            value={value.carrier}
            onChange={(e) => onChange({ ...value, carrier: e.target.value })}
            placeholder={t("Carrier")}
            aria-label={t("Carrier")}
            disabled={disabled}
            className="rounded-lg border border-gray-300 px-2.5 py-2 text-[12px] focus:border-primary-500 focus:ring-primary-500"
          />
        ) : (
          <select
            value={value.carrier}
            onChange={(e) => {
              if (e.target.value === "__other") {
                setOtherCarrier(true);
                onChange({ ...value, carrier: "" });
              } else {
                onChange({ ...value, carrier: e.target.value });
              }
            }}
            aria-label={t("Carrier")}
            disabled={disabled}
            className="rounded-lg border border-gray-300 bg-white py-2 pl-2.5 pr-8 text-[12px] focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="">{t("Carrier")}</option>
            {CARRIERS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
            <option value="__other">{t("Other…")}</option>
          </select>
        )}
        <input
          type="text"
          value={value.tracking}
          onChange={(e) => onChange({ ...value, tracking: e.target.value })}
          placeholder={t("Tracking number")}
          aria-label={t("Tracking number")}
          disabled={disabled}
          className="min-w-0 rounded-lg border border-gray-300 px-3 py-2 font-mono text-[12px] focus:border-primary-500 focus:ring-primary-500"
        />
      </div>
      <div className="flex flex-wrap items-center gap-2 text-[12px]">
        <label className="cursor-pointer rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-gray-700 hover:bg-gray-50">
          {value.labelFileName ? t("Change label file") : t("Choose label file (PDF or image)")}
          <input type="file" accept={LABEL_FILE_TYPES} onChange={pickFile} disabled={disabled} className="sr-only" />
        </label>
        {value.labelFileName && <span className="truncate text-gray-700">{value.labelFileName}</span>}
        {fileError && <span className="text-red-700">{fileError}</span>}
      </div>
    </div>
  );
}
